import React, { useState } from "react";
import { auth } from "./firebaseConfig";
import {
  createUserWithEmailAndPassword,
  signInWithPopup,
  GoogleAuthProvider,
} from "firebase/auth";
import { useNavigate, Link } from "react-router-dom";
import Header from "./components/Header";

/* -------------------- Friendly Error Mapper -------------------- */
function getFriendlyErrorMessage(code) {
  switch (code) {
    case "auth/invalid-email":
      return "Please enter a valid email address.";
    case "auth/email-already-in-use":
      return "This email is already registered. Try logging in instead.";
    case "auth/weak-password":
      return "Your password should be at least 6 characters.";
    case "auth/popup-closed-by-user":
      return "Google sign-in was cancelled.";
    case "auth/network-request-failed":
      return "Network error. Please check your connection.";
    default:
      return "Something went wrong. Please try again.";
  }
}

/* -------------------- MAIN COMPONENT -------------------- */
export default function SignUpForm() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const navigate = useNavigate();

  const handleEmailSignUp = async (e) => {
    e.preventDefault();
    setError("");
    try {
      await createUserWithEmailAndPassword(auth, email, password);
      navigate("/"); // redirect to home after signup
    } catch (err) {
      setError(getFriendlyErrorMessage(err.code));
    }
  };

  const handleGoogleSignIn = async () => {
    setError("");
    const provider = new GoogleAuthProvider();
    try {
      await signInWithPopup(auth, provider);
      navigate("/");
    } catch (err) {
      setError(getFriendlyErrorMessage(err.code));
    }
  };

  return (
    <div className="relative min-h-screen flex flex-col text-gray-800 overflow-hidden">
      {/* === FIXED LIGHT BACKGROUND === */}
      <div className="fixed inset-0 -z-10" style={{ background: 'linear-gradient(to bottom, #FFFADF 0%, rgba(255, 250, 223, 0.8) 50%, #FFFADF 100%)' }} />
      <div className="fixed inset-0 -z-10" style={{ background: 'radial-gradient(circle at 50% 20%, rgba(143, 179, 211, 0.12), transparent 60%)' }} />
      <div
        className="fixed inset-0 -z-10 opacity-[.04] pointer-events-none"
        style={{ backgroundImage: "url(/images/noise-512.png)" }}
      />

      {/* --- Global Header --- */}
      <Header />

      {/* --- Content --- */}
      <div className="flex-grow flex items-center justify-center px-4 mt-20 mb-12 relative z-10">
        <div className="w-full max-w-md bg-white border border-gray-200 rounded-3xl shadow-md p-8 md:p-10">
          <h2 className="text-3xl font-semibold text-center text-gray-900 mb-2">
            Create Account
          </h2>
          <p className="text-gray-600 text-center mb-8">
            Please use the same email you used when purchasing your tour.
          </p>

          <form onSubmit={handleEmailSignUp} className="space-y-4">
            <input
              type="email"
              placeholder="Email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              className="w-full p-3 rounded-lg border text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 transition"
              style={{ borderColor: '#E4B4A6', '--tw-ring-color': 'rgba(143, 179, 211, 0.6)' }}
            />
            <input
              type="password"
              placeholder="Password (min. 6 characters)"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              className="w-full p-3 rounded-lg border text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 transition"
              style={{ borderColor: '#E4B4A6', '--tw-ring-color': 'rgba(143, 179, 211, 0.6)' }}
            />
            <button
              type="submit"
              className="w-full py-3 rounded-lg font-semibold text-white shadow-md hover:shadow-lg active:scale-95 transition-all hover:opacity-90"
              style={{ backgroundColor: '#F8662B' }}
            >
              Sign Up with Email
            </button>
          </form>

          <div className="my-6 text-center text-gray-400 text-sm">or</div>

          <button
            onClick={handleGoogleSignIn}
            className="w-full flex items-center justify-center gap-3 py-3 rounded-lg border border-gray-300 text-gray-700 bg-white hover:bg-gray-50 transition-all font-semibold shadow-sm"
          >
            <img
              src="https://www.svgrepo.com/show/355037/google.svg"
              alt="Google"
              className="w-5 h-5"
            />
            Continue with Google
          </button>

          {error && (
            <p className="text-red-500 mt-4 text-sm text-center font-medium">
              {error}
            </p>
          )}

          <p className="text-center text-gray-700 mt-6 text-sm">
            Already have an account?{" "}
            <Link
              to="/login"
              className="font-semibold transition hover:opacity-70"
              style={{ color: '#F8662B' }}
            >
              Log in here
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
}
